import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

interface StoredRoute {
  url: string;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class RoutePersistenceService {
  private readonly STORAGE_KEY = 'lastVisitedRoute';
  private readonly EXPIRY_HOURS = 12;
  private readonly EXCLUDED_ROUTES = ['/login', '/register', '/forgot-password', '/reset-password'];

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.initializeRouteTracking();
  }

  /**
   * Initialize route tracking to store last visited route
   */
  private initializeRouteTracking(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      // Don't store excluded routes (login, auth pages, etc.)
      if (this.shouldStoreRoute(event.urlAfterRedirects)) {
        this.storeRoute(event.urlAfterRedirects);
      }
    });
  }

  /**
   * Check if route should be stored
   */
  private shouldStoreRoute(url: string): boolean {
    // Don't store if URL is in excluded routes
    return !this.EXCLUDED_ROUTES.some(route => url.startsWith(route));
  }

  /**
   * Store route in localStorage with timestamp
   */
  private storeRoute(url: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const storedRoute: StoredRoute = {
      url: url,
      timestamp: Date.now()
    };

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(storedRoute));
    } catch (error) {
      console.error('Failed to store route:', error);
    }
  }

  /**
   * Get last visited route if within expiry time
   * @returns Last route URL or null if expired/not found
   */
  getLastVisitedRoute(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        return null;
      }

      const storedRoute: StoredRoute = JSON.parse(stored);
      const now = Date.now();
      const expiryTime = this.EXPIRY_HOURS * 60 * 60 * 1000; // Convert hours to milliseconds

      // Check if route is still valid (within 12 hours)
      if (now - storedRoute.timestamp <= expiryTime) {
        return storedRoute.url;
      } else {
        // Route expired, clear it
        this.clearStoredRoute();
        return null;
      }
    } catch (error) {
      console.error('Failed to retrieve stored route:', error);
      return null;
    }
  }

  /**
   * Clear stored route from localStorage
   */
  clearStoredRoute(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear stored route:', error);
    }
  }

  /**
   * Get time since last visit in hours
   */
  getTimeSinceLastVisit(): number | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        return null;
      }

      const storedRoute: StoredRoute = JSON.parse(stored);
      const now = Date.now();
      const hoursSince = (now - storedRoute.timestamp) / (1000 * 60 * 60);

      return hoursSince;
    } catch (error) {
      return null;
    }
  }
}
